import BaseModel from './BaseModel';
import DayType from './DayType';
import Equipment from './Equipment';
import ProjectPart from './ProjectPart';
import Transaction from './Transaction';
import Purchase from './Purchase';
import * as AT from '../../components/container/attributes';
import {HitOnlineEnums} from '@/../../app/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';

export default class Cost extends BaseModel {
  constructor({
    id,
    number,
    designation,
    active = true,
    memo = undefined,
    costDate = undefined,
    supplier = undefined,
    projectPart = undefined,
    equipment = undefined,
    transaction = undefined,
    dayType = undefined,
    purchase = undefined,
    type = undefined,
  }) {
    super(id, number, designation);
    this.active = active;
    this.memo = memo;
    this.costDate = costDate;
    this.supplier = supplier;
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.equipment = equipment ? new Equipment(equipment) : null;
    this.transaction = transaction ? new Transaction(transaction) : null;
    this.dayType = dayType ? new DayType(dayType) : null;
    this.purchase = purchase ? new Purchase(purchase) : null;
    this.type = type
      ? HitOnlineEnums.getEnumValue('COST_TYPE', type, translate)
      : null;
  }

  static get config() {
    return {
      addable: false,
      duplicatable: false,
      importable: false,
      browsable: false,
      deletable: false,
    };
  }
  get config() {
    return Transaction.config;
  }

  static get joinAttrsKey() {
    return [
      'number',
      'designation',
      'projectPart',
      'equipment',
      'dayType',
      'purchase',
    ];
  }
  static get fkName() {
    return 'cost_id';
  }
  static get apiRoute() {
    return 'cost';
  }
  static get detailPageRoute() {
    return 'costsView';
  }
  static get listPageRoute() {
    return 'costsView';
  }
  get fkName() {
    return Cost.fkName;
  }
  get apiRoute() {
    return Cost.apiRoute;
  }
  get listPageRoute() {
    return Cost.listPageRoute;
  }
  get detailPageRoute() {
    return Cost.detailPageRoute;
  }

  static get orderKey() {
    return 'costDate';
  }
  static get orderOrder() {
    return 'desc';
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'active',
      'costDate',
      'totalAmount',
    ]);
  }

  static get deliveryNoteDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'designation',
        'projectPart',
        'supplier',
        'costDate',
        'totalAmount',
        'purchase',
      ],
      ['active', 'memo'],
    );
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'designation',
        'active',
        'supplier',
        'costDate',
        'totalAmount',
        'transaction',
        'creationDate',
        'modificationDate',
      ],
      ['projectPart', 'staff', 'equipment', 'form', 'injectedFromForm', 'type'],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'costDate',
        'supplier',
        'active',
      ]),
    };
  }
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE({}, {maxWidth: '0.8fr'}),
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '1.6fr'}),
      this.ACTIVE_ATTRIBUTE({}, {hideByDefault: true}),
      new AT.StringAttribute({
        name: 'memo',
        tableProperties: {
          header: 'hit-base.common.memo',
        },
      }),
      new AT.DateAttribute({
        name: 'costDate',
        column: 'cost_date',
        tableProperties: {
          header: 'hit-app.common.date',
          maxWidth: '0.8fr',
        },
      }),
      new AT.FkAddressAttribute({
        name: 'supplier',
        column: 'address_id',
        joinPath: ['address!fk_cost_address_id_address'],
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.supplier',
          maxWidth: '1fr',
        },
        filters: {supplier: 'is.true'},
      }),
      new AT.NumberAttribute({
        name: 'totalAmount',
        column: 'cost_data(amount.sum())',
        readOnly: true,
        transform: (item) =>
          Array.isArray(item) && item[0] && item[0].sum ? item[0].sum : 0,
        tableProperties: {
          header: 'hit-app.common.amount',
          maxWidth: '1fr',
          readOnly: true,
        },
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        joinPath: ['project_part!fk_cost_project_part_id_project_part'],
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.project-part',
          maxWidth: '1fr',
        },
      }),
      new AT.FkEquipmentAttribute({
        name: 'equipment',
        joinPath: ['equipment!fk_cost_equipment_id_equipment'],
        onlyFetchDesignation: true,
      }),
      new AT.FkStaffAttribute({
        name: 'staff',
        joinPath: ['staff!fk_cost_staff_id_staff'],
        onlyFetchDesignation: true,
      }),
      new AT.FkTransactionAttribute({
        name: 'transaction',
        joinPath: ['transaction!fk_cost_transaction_id_transaction'],
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-base.common.transaction',
          readOnly: true,
          maxWidth: '1.2fr',
        },
      }),
      new AT.NotFetchedAttribute({
        name: 'transactionDesignation',
        transform: (item) => item?.transaction?.designation,
        tableProperties: {
          header: 'hit-base.common.transaction',
          readOnly: true,
          maxWidth: '1.2fr',
        },
      }),
      new AT.FkFormAttribute({
        name: 'form',
        joinPath: ['form!fk_cost_form_id_form'],
        onlyFetchDesignation: true,
      }),
      new AT.BooleanAttribute({
        name: 'injectedFromForm',
        readOnly: true,
        column: 'form_id',
        transform: (item) => Boolean(item),
      }),
      new AT.FkDayTypeAttribute({
        name: 'dayType',
        joinPath: ['day_type!fk_cost_day_type_id_day_type'],
      }),
      new AT.EnumerationAttribute({
        name: 'type',
        enumType: 'COST_TYPE',
        enumClass: HitOnlineEnums,
        creationValue: 'COST',
        tableProperties: {
          header: 'hit-app.common.type',
        },
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.FkPurchaseAttribute({
        name: 'purchase',
        joinPath: ['purchase!fk_cost_purchase_id_purchase'],
        onlyFetchDesignation: true,
        tableProperties: {
          header: 'hit-app.common.purchase',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'supplierNo',
        column: 'supplier_no',
        tableProperties: {
          header: 'hit-app.common.supplier-reference',
          maxWidth: '2fr',
        },
      }),
      new AT.StringAttribute({
        name: 'registrationNumber',
        column: 'registration_no',
        tableProperties: {
          header: 'hit-app.common.vehicle-no',
          maxWidth: '2fr',
        },
      }),
    ];
  }
}
